<template>
  <loading-page
    v-if="fetchingBuckets && fetchingExternalAccounts"
    full
  />
  <div
    v-else
    class="table-wrapper top-fade-in buckets-table-wrapper"
  >
    <div class="d-flex justify-content-between">
      <div>
        <b-button
          variant="bg-primary"
          :disabled="!!selectedAccount.bucketName"
          @click="createBucket()"
        >
          <GjIcon
            class="icon-left"
            name="Plus"
          />
          Create Bucket
        </b-button>

      </div>
      <b-button
        variant="bg-white"
        class="mr-0"
        :disabled="!!selectedAccount.bucketName"
        @click="onRefresh()"
      >
        <GjIcon
          class="icon-left"
          name="Refresh"
        />
        Refresh
      </b-button>
    </div>
    <div
      :class="
        currentBreakPoint === 'lg' || currentBreakPoint === 'xl'
          ? 'default-table'
          : 'mt-2'
      "
    >
      <div class="table-actions d-flex">
        <b-dropdown
          v-if="selectedAccount"
          id="object-options"
          variant="bg-white"
          no-caret
        >
          <template
            #button-content
          >
            <img
              :src="selectedAccount.parentId === '0' ? require('@/assets/images/logo/Captain.svg') : selectedAccount.providerImageUrl"
              width="30px"
              height="30px"
              alt="Storage External Account"
              class="mr-1"
            >
            <span><b>{{ selectedAccount.parentId === '0' ? 'Captain' : selectedAccount.username }}</b></span>
            <GjIcon
              class="ml-1"
              name="ArrowDown"
            />
          </template>
          <ul
            v-for="(account, property) in externalAccounts"
            :key="property"
            class="list-unstyled m-0 p-0"
          >
            <b-dropdown-item
              @click="account.id !== selectedAccount.id ? changeSelectedAccount(account) : ''"
            >
              <div class="d-flex align-items-center">
                <img
                  :src="account.parentId === '0' ? require('@/assets/images/logo/Captain.svg') : account.providerImageUrl"
                  width="30px"
                  height="30px"
                  alt=""
                  class="mr-1"
                >
                <div class="d-flex align-items-start flex-column">
                  <span class=""><strong>{{ account.parentId === '0' ? 'Captain ' : account.username }}</strong></span>
                  <span class="text-secondary text-sm"><strong>{{ account.bucketName ? account.bucketName : '' }}</strong></span>
                </div>
              </div>

            </b-dropdown-item>

          </ul>
        </b-dropdown>
        <b-input-group class="w-auto mb-1 mb-lg-0 ml-auto">
          <b-input-group-prepend is-text>
            <b-icon-search />
          </b-input-group-prepend>
          <b-form-input
            id="filterInput"
            v-model="filterTerm"
            type="search"
            class="mr-0"
            placeholder="Type to Search"
          />
        </b-input-group>
      </div>
      <div class="position-relative d-none d-lg-block">
        <b-table
          ref="table"
          :items="items"
          :fields="fields"
          :class="{ loaded: !fetchingBuckets || fetchingExternalAccounts }"
          :filter="filterTerm"
          :filter-included-fields="filterOn"
          show-empty
          responsive
          :foot-clone="items.length > 1"
        >
          <template #cell(name)="data">
            <div class="d-flex align-items-start">
              <span
                class="cursor-pointer"
                @click="data.item.status === null ? openBucket(data) : ''"
              >
                <GjIcon
                  size="35"
                  name="Record"
                />
              </span>
              <div class="d-flex flex-column ml-1">
                <a
                  :class="
                    data.item.status === null ? 'text-primary' : 'text-main'
                  "
                  @click="
                    data.item.status === null ? openBucket(data) : ''
                  "
                >{{ data.item.bucketName ? data.item.bucketName : data.value }}</a>
                <a
                  v-clipboard:copy="checkEndpoint(data.item.endpoint)"
                  v-clipboard:success="copySuccess"
                  class="text-sm"
                >
                  {{ checkEndpoint(data.item.endpoint) }}
                  <GjIcon
                    size="15"
                    name="Copy"
                  />
                </a>
              </div>
            </div>
          </template>
          <template #cell(objects)="data">
            {{ selectedAccount.accountType === 0 ? data.value.toLocaleString() : '-' }}
          </template>
          <template #cell(bytes)="data">
            {{ selectedAccount.accountType === 0 ? formatBytes(data.value) : '-' }}
          </template>
          <template #cell(status)="data">
            <div class="d-flex align-items-center">
              <span v-if="data.item.status">
                {{ ` ${data.item.processingType} - ${data.item.status} ` }}
              </span>
              <span v-else-if="data.item.processingType">
                {{ data.item.processingType }}
              </span>
              <span
                v-else
                class="text-success"
              >
                Healthy
              </span>
            </div>
          </template>
          <template #cell(actions)="data">
            <bucket-actions
              :bucket-enums="bucketEnums"
              :bucket-status="data.item.status"
              :move="false"
              edit
              class="ml-auto"
              @delete="openDeleteBucket(data.item)"
              @empty="emptyBucket(data.item)"
              @open="openBucket(data)"
              @settings="openBucketSettings(data.item.name)"
            />
          </template>
          <template #empty>
            <div
              v-if="fetchingBuckets"
              class="loading-table text-center py-3"
            >
              <b-spinner variant="primary" />
            </div>
            <template v-else-if="somethingWentWrong">
              <div
                key="something-wrong"
                class="py-4 text-center"
              >
                Something went wrong. Please try again later.
              </div>
            </template>
            <template v-else>
              <no-data
                no-data-text="No buckets have been created yet..."
                create-text="+ Create Bucket"
                access-page="Buckets"
                icon="Record"
                :has-permissions="hasPermissions"
                @action="createBucket()"
              />
            </template>
          </template>
          <template #foot(name)>
            {{ getSum() }}
          </template>
          <template #foot(status)>
            {{ getSum() }}
          </template>
          <template #foot(objects)="data">
            <span>{{ getSum(data.field.key).toLocaleString() }}</span>
          </template>
          <template #foot(bytes)="data">
            <span>{{ formatBytes(getSum(data.field.key)) }}</span>
          </template>
        </b-table>
        <table-busy v-if="fetchingBuckets && items.length > 0" />
      </div>
      <div class="position-relative d-lg-none">
        <card
          v-for="item in items"
          :key="item.id"
          :fields="fields"
          :item="item"
          :col-number="4"
        >
          <template #options>
            <b-dropdown
              id="dropdown-1"
              right
              variant="white"
            >
              <template #button-content>
                <GjIcon name="Moreoptions" />
              </template>
              <b-dropdown-item @click="openBucket(item)">
                <GjIcon name="Files" /> Files
              </b-dropdown-item>
              <b-dropdown-item @click="openBucketSettings(item.name)">
                <GjIcon name="Settings" /> Settings
              </b-dropdown-item>
              <b-dropdown-item
                variant="danger"
                @click="openDeleteBucket(item)"
              >
                <GjIcon name="Delete" /> Delete
              </b-dropdown-item>
              <b-dropdown-item
                variant="danger"
                @click="emptyBucket(item)"
              >
                <GjIcon name="MinusAlt" /> Empty
              </b-dropdown-item>
            </b-dropdown>
          </template>

          <template #name>
            <div class="d-flex align-items-start">
              <div class="d-flex flex-column">
                <a
                  :class="{ 'text-primary': item.status === null }"
                  @click="item.status === null ? openBucket(item) : ''"
                >{{ item.name }}</a>
              </div>
            </div>
          </template>

          <template #objects>
            {{ item.objects ? item.objects.toLocaleString() : '-' }}
          </template>
          <template #bytes>
            {{ formatBytes(item.bytes) }}
          </template>
          <template #status>
            <div class="d-flex align-items-center">
              <span v-if="item.status">
                {{ ` ${item.processingType} - ${item.status} ` }}
              </span>
              <span v-else-if="item.processingType">
                {{ item.processingType }}
              </span>
              <span
                v-else
                class="text-success"
              >
                Healthy
              </span>
            </div>
          </template>
        </card>
        <div
          v-if="fetchingBuckets"
          class="mobile-loading"
        >
          <table-busy />
        </div>
        <template v-if="!fetchingBuckets && items.length < 1">
          <paper-simple class="top-fade-in">
            <no-data
              class="text-center"
              no-data-text="No buckets have been created yet..."
              create-text="+ Create Bucket"
              access-page="Buckets"
              icon="Record"
              :has-permissions="hasPermissions"
              @action="createBucket()"
            />
          </paper-simple>
        </template>
      </div>
    </div>
    <delete-confirmation
      v-if="bucketName"
      :name="bucketName"
      :show="!!bucketName"
      @close="bucketName = null"
      @delete="onDelete()"
    />
  </div>
</template>
<script>
import { BIconSearch } from 'bootstrap-vue'

export default {
  name: 'Buckets',
  components: {
    BucketActions: () => import('./helpers/BucketActions.vue'),
    DeleteConfirmation: () => import('../objects/settings/DeleteConfirmation.vue'),
    Card: () => import('@/components/card/Card.vue'),
    BIconSearch,
  },
  data() {
    return {
      loading: true,
      currentPage: 1,
      fields: [
        {
          key: 'name',
          label: 'Name',
          variant: 'fill',
          isPrimary: true,
        },
        {
          key: 'objects',
          label: 'Objects',
          variant: 'time',
          isPrimary: true,
        },
        {
          key: 'bytes',
          label: 'Size',
          variant: 'small',
          isPrimary: true,
        },
        { key: 'status', label: 'Status', variant: 'medium' },
        {
          key: 'actions',
          label: '',
          tdClass: 'file-actions',
          variant: 'one-button',
          hide: true,
        },
      ],
      showDropdown: false,
      somethingWentWrong: false,
      bucketName: null,
      hasPermissions: true,
      filterTerm: null,
      filterOn: ['name'],
    }
  },
  computed: {
    externalAccounts() {
      return this.$store.state.externalAccount.externalAccounts
    },
    fetchingExternalAccounts() {
      return this.$store.state.externalAccount.fetchingExternalAccounts
    },
    fetchingBuckets() {
      return this.$store.state.accountBuckets.fetchingBuckets
    },
    bucketEnums() {
      return this.$store.state.accountBuckets.bucketEnums
    },
    currentBreakPoint() {
      return this.$store.getters['app/currentBreakPoint']
    },
    previousRouteName() {
      return this.$store.state.app.previousRouteName
    },
    items: {
      get() {
        return this.$store.state.accountBuckets.buckets
      },
      set(account) {
        this.$store.commit('accountBuckets/UPDATE_BUCKETS', [{ ...account }])
      },
    },
    selectedAccount: {
      get() {
        return this.$store.state.externalAccount.externalAccount || null
      },
      set(account) {
        this.$store.commit('externalAccount/CHANGE_ACCOUNT', account)
      },
    },
    accountId: {
      get() {
        return this.$store.state.externalAccount.accountId
      },
      set(val) {
        this.$store.commit('externalAccount/SET_ACCOUNT_ID', val)
      },
    },
  },
  created() {
    this.accountId = 0
    this.getAccounts()
    this.fetchBucketEnums()
  },
  methods: {
    getBuckets() {
      this.$store
        .dispatch('accountBuckets/fetchBuckets', this.accountId)
        .catch(error => {
          const { status } = error.response || null
          if (status === 424) {
            this.$store.commit('accountBuckets/SET_SHOW_INTRO', true)
            this.$store.commit('buckets/SET_SHOW_INTRO', true)
            return
          }
          if (status === 426 || status === 428) {
            this.$store.commit('accountBuckets/SET_SERVICE_UNAVAILABLE', status === 428)
            this.$store.commit('buckets/SET_SERVICE_UNAVAILABLE', status === 428)
            return
          }
          if (status === 403) {
            this.hasPermissions = false
            return
          }
          this.errorToast(
            error.response?.data?.errors[0]
              || 'Something went wrong while fetching buckets.',
          )
        })
    },
    getAccounts() {
      this.$store
        .dispatch('externalAccount/fetchAccounts')
        .then(() => {
          if (this.externalAccounts.length > 0) {
            const account = this.externalAccounts[0]
            this.changeSelectedAccount(account)
          }
        })
        .catch(error => {
          this.errorToast(
            error.response?.data?.errors
              || 'Something went wrong while fetching all external accounts.',
          )
        })
    },
    changeSelectedAccount(account) {
      if (account) {
        this.selectedAccount = account
        this.accountId = account?.id || 0
      }
      if (account?.bucketName) {
        const item = { status: null, name: account.bucketName, ...account }
        this.items = item
        this.$store.commit('accountBuckets/CANCEL_FETCHING_BUCKETS')
      } else {
        this.getBuckets()
      }

      return this.selectedAccount
    },
    fetchBucketEnums() {
      this.$store
        .dispatch('accountBuckets/fetchBucketEnums', this.accountId)
        .catch(error => {
          const { status } = error.response || null
          if (status === 424) {
            this.$store.commit('accountBuckets/SET_SHOW_INTRO', true)
            this.$store.commit('buckets/SET_SHOW_INTRO', true)
            return
          }
          this.errorToast(
            error.response?.data?.errors[0]
              || 'Something went wrong while fetching buckets.',
          )
        })
        .finally(() => {
          this.loading = false
        })
    },
    createBucket() {
      this.$router.push({ name: 'create-bucket', params: { accountId: this.accountId } })
    },
    openBucket(data) {
      let routeName = null
      if (data.name) routeName = data.name
      if (data.item.name) routeName = data.item.name

      this.$router.push({ name: 'object-files', params: { id: routeName, accountId: this.accountId } })
    },
    openBucketSettings(id) {
      this.$router.push({ name: 'bucket-settings', params: { id, accountId: this.accountId } })
    },
    emptyBucket(item) {
      this.confirmDelete(
        'Empty Bucket',
        `Are you sure you want to empty "${item.name}" bucket?`,
        'Empty',
      ).then(ok => {
        if (ok) {
          this.$store
            .dispatch('accountBuckets/emptyBucket', {
              accountId: this.accountId,
              bucket: {
                name: item.name,
              },
            })
            .then(() => {})
            .catch(err => {
              this.errorToast(
                err.response?.data?.errors[0] || 'Something went wrong.',
              )
            })
        }
      })
    },
    openDeleteBucket(item) {
      this.bucketName = item.name
    },
    onDelete() {
      this.$store
        .dispatch('accountBuckets/deleteBucket', { accountId: this.accountId, bucket: { name: this.bucketName } })
        .then(() => {
          this.successToast('Bucket was deleted')
          this.bucketName = null
          this.getBuckets()
        })
        .catch(error => {
          this.errorToast(
            error.response?.data?.errors[0] || 'Something went wrong.',
          )
        })
    },
    getSum(name) {
      const el = this.filterTerm
        ? this.items.filter(e => e.name.includes(this.filterTerm))
        : this.items
      return name ? el.reduce((accum, item) => accum + item[name], 0) : ''
    },
    checkEndpoint(val) {
      return /^https?:/.test(val) ? val : `https://${val}`
    },
    onRefresh() {
      if (!this.selectedAccount?.bucketName) {
        this.getBuckets()
      }
    },
  },
}
</script>
<style lang="scss" scoped>
.default-table::v-deep {
  .btn {
    height: auto;
  }
  .file-actions {
    overflow: visible;
  }
}
</style>
